export type ReorderGroup = {
  id: string
  keys: string[]
}

export type ReorderAxis = 'vertical' | 'horizontal'

export function cloneReorderGroups(groups: ReorderGroup[]): ReorderGroup[] {
  return groups.map(function copyGroup(group) {
    return { id: group.id, keys: [...group.keys] }
  })
}

export function moveReorderItem(
  groups: ReorderGroup[],
  groupId: string,
  fromIndex: number,
  toIndex: number,
): ReorderGroup[] {
  return groups.map(function moveInsideGroup(group) {
    if (group.id !== groupId || !canMove(group.keys.length, fromIndex, toIndex)) {
      return group
    }
    const keys = [...group.keys]
    const moved = keys[fromIndex]
    if (moved === undefined) {
      return group
    }
    keys.splice(fromIndex, 1)
    keys.splice(toIndex, 0, moved)
    return { id: group.id, keys }
  })
}

function canMove(length: number, fromIndex: number, toIndex: number): boolean {
  if (fromIndex === toIndex) {
    return false
  }
  return fromIndex >= 0 && toIndex >= 0 && fromIndex < length && toIndex < length
}

function sameKeyMembership(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  const pending = [...right]
  for (const key of left) {
    const index = pending.indexOf(key)
    if (index < 0) {
      return false
    }
    pending.splice(index, 1)
  }
  return true
}

/** Même groupes et mêmes libellés, mais pas dans le même ordre. */
export function isReorderPermutation(initial: ReorderGroup[], current: ReorderGroup[]): boolean {
  if (reorderGroupsEqual(initial, current) || initial.length !== current.length) {
    return false
  }
  return initial.every(function sameMembers(group, index) {
    const other = current[index]
    if (!other || other.id !== group.id) {
      return false
    }
    return sameKeyMembership(group.keys, other.keys)
  })
}

export function reorderGroupsEqual(left: ReorderGroup[], right: ReorderGroup[]): boolean {
  if (left.length !== right.length) {
    return false
  }
  return left.every(function sameGroup(group, index) {
    const other = right[index]
    if (!other || other.id !== group.id || other.keys.length !== group.keys.length) {
      return false
    }
    return group.keys.every(function sameKey(key, keyIndex) {
      return key === other.keys[keyIndex]
    })
  })
}

export function countMovedKeys(current: ReorderGroup[], initial: ReorderGroup[]): number {
  let moved = 0
  for (const group of current) {
    moved += countMovedInGroup(group, initial)
  }
  return moved
}

function countMovedInGroup(group: ReorderGroup, initial: ReorderGroup[]): number {
  const start = initial.find(function sameId(candidate) {
    return candidate.id === group.id
  })
  if (!start) {
    return 0
  }
  let moved = 0
  for (let index = 0; index < group.keys.length; index += 1) {
    if (group.keys[index] !== start.keys[index]) {
      moved += 1
    }
  }
  return moved
}

export function keyboardDestinationIndex(
  length: number,
  fromIndex: number,
  key: string,
  axis: ReorderAxis,
): number | null {
  const previousKey = axis === 'vertical' ? 'ArrowUp' : 'ArrowLeft'
  const nextKey = axis === 'vertical' ? 'ArrowDown' : 'ArrowRight'
  if (key === previousKey) {
    return Math.max(0, fromIndex - 1)
  }
  if (key === nextKey) {
    return Math.min(length - 1, fromIndex + 1)
  }
  if (key === 'Home') {
    return 0
  }
  if (key === 'End') {
    return Math.max(0, length - 1)
  }
  return null
}

export function dirtyReorderGroups(current: ReorderGroup[], initial: ReorderGroup[]): ReorderGroup[] {
  return current.filter(function isDirty(group) {
    const start = initial.find(function sameId(candidate) {
      return candidate.id === group.id
    })
    if (!start) {
      return false
    }
    return !reorderGroupsEqual([group], [start])
  })
}

export function replaceGroupKeys(groups: ReorderGroup[], groupId: string, keys: string[]): ReorderGroup[] {
  return groups.map(function replace(group) {
    if (group.id !== groupId) {
      return group
    }
    return { id: group.id, keys }
  })
}

export function movedItemsLabel(count: number): string {
  if (count <= 0) {
    return 'Aucun élément déplacé'
  }
  if (count === 1) {
    return '1 élément déplacé'
  }
  return `${count} éléments déplacés`
}
